import React, { useEffect, useRef } from "react";

// Lightweight Leaflet map loaded via CDN once. Renders pin markers for every prop.
let LEAFLET_LOADED = null;

function loadLeaflet() {
  if (LEAFLET_LOADED) return LEAFLET_LOADED;
  LEAFLET_LOADED = new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.L) return resolve(window.L);

    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(css);

    const js = document.createElement("script");
    js.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    js.async = true;
    js.onload = () => resolve(window.L);
    document.head.appendChild(js);
  });
  return LEAFLET_LOADED;
}

export default function IndiaMap({ items = [], onMarkerClick, height = 520 }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layerRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    loadLeaflet().then((L) => {
      if (!mounted || !L || !containerRef.current) return;

      if (!mapRef.current) {
        mapRef.current = L.map(containerRef.current, {
          center: [22.9734, 78.6569],
          zoom: 5,
          scrollWheelZoom: false,
          zoomControl: true,
          attributionControl: false,
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OSM",
          maxZoom: 18,
        }).addTo(mapRef.current);
      }

      if (layerRef.current) {
        layerRef.current.clearLayers();
      } else {
        layerRef.current = L.layerGroup().addTo(mapRef.current);
      }

      const valid = items.filter((p) => p.lat && p.lng);
      const icon = L.divIcon({
        className: "",
        html: `<div style="background:#5D33F6;width:14px;height:14px;border-radius:9999px;border:3px solid white;box-shadow:0 4px 14px rgba(93,51,246,.45);"></div>`,
        iconSize: [14, 14],
        iconAnchor: [7, 7],
      });
      valid.forEach((p) => {
        const m = L.marker([p.lat, p.lng], { icon }).addTo(layerRef.current);
        const html = `
          <div style="font-family:'Plus Jakarta Sans',sans-serif;min-width:200px">
            <div style="font-weight:700;color:#5D33F6;font-size:11px;letter-spacing:.12em;text-transform:uppercase">${p.property_type || "Retail"}</div>
            <div style="font-weight:600;font-size:14px;margin-top:2px;font-family:'Outfit',sans-serif">${p.building_name}</div>
            <div style="color:#525252;font-size:12px;margin-top:2px">${p.area}, ${p.city}</div>
            <div style="margin-top:6px;font-size:12px"><b>₹${p.rent_per_sqft}/sqft</b> · ${p.carpet_total} sqft · ${p.floor}</div>
          </div>`;
        m.bindPopup(html);
        if (onMarkerClick) m.on("click", () => onMarkerClick(p));
      });

      if (valid.length > 0) {
        const bounds = L.latLngBounds(valid.map((p) => [p.lat, p.lng]));
        mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
      }
    });

    return () => { mounted = false; };
  }, [items, onMarkerClick]);

  return (
    <div
      ref={containerRef}
      data-testid="india-map"
      className="w-full rounded-2xl overflow-hidden border border-neutral-200"
      style={{ height: `${height}px`, background: "#F6F4FF" }}
    />
  );
}
